import EmberService, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { APIData, Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { UserSettingsKey } from 'webapp/enums/user-settings';
import { sortBy } from 'webapp/utils/common';

import AccountPopupProductUpdatesComponent from 'webapp/components/account/popup-product-updates';

import type PopupModal from 'webapp/models-custom/popup/modal';

import type { RSjqXHR } from 'webapp/services/request';

export interface ProductUpdate {
	id: number;
	created: number;
	modified: number;
	data: {
		productUpdate: {
			image?: string;
			shortText?: string;
			showInPopup?: boolean;
			tag?: string;
			text?: boolean;
			title?: string;
			url?: string;
			urlName?: string;
		};
	};
}

export default class ProductUpdatesService extends EmberService {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('automated-popup-queue')
	declare automatedPopupQueueService: Service.AutomatedPopupQueue;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('navigation') declare navigationService: Service.Navigation;

	@tracked isLoading = false;
	@tracked lastProductUpdate = 0;

	@tracked productUpdates: ProductUpdate[] = [];

	private updatesLoadingXHR: RSjqXHR | null = null;

	getProductUpdates(): Promise<unknown> {
		// make API request for product updates

		// if request already in progress, cancel it
		const updatesLoadingXHR = this.updatesLoadingXHR;
		if (updatesLoadingXHR?.readyState !== 4) {
			updatesLoadingXHR?.abort();
		}

		this.isLoading = true;

		const promise = this.requestService.api<APIData.ProductUpdates.ListResponse>(
			'GET',
			'/product-updates',
			null,
			{
				timeout: 60000,
				withoutPrefix: true,
			},
		);

		this.updatesLoadingXHR = promise.jqPromise as RSjqXHR;

		promise
			.then((reply) => {
				this.productUpdates = sortBy(reply.activityMessages, 'created').reverse();
				this.isLoading = false;
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));

		return promise;
	}

	addModalToAutomatedPopupQueue(): void {
		const requiredPromises = [];
		if (this.productUpdates.length === 0) {
			requiredPromises.push(this.getProductUpdates());
		}

		const created = this.accountService.currentSpace?.created || 0;

		const isAccountOlderThan14Days = moment().diff(moment.unix(created), 'days') > 14;
		const isUserOlderThan7Days = moment().diff(moment.unix(created), 'days') > 7;

		this.automatedPopupQueueService.queuePopup({
			popup: this.createModal(),
			requiredPromises,
			routeBlacklist: ['boards.view.board', 'reports.view.report', 'datawall'],
			checkAddConditions: () => {
				return (
					this.lastProductUpdate > 0 &&
					this.currentUserService.hasNotSeenNewProductUpdates &&
					!this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins &&
					isAccountOlderThan14Days &&
					isUserOlderThan7Days
				);
			},
			checkStartConditions: () => {
				const hasNewUpdatesToBeOpenedInPopup = this.productUpdates.some(
					(productUpdate) =>
						productUpdate.created >= this.userSettingsService.lastSeenProductUpdates &&
						productUpdate.data.productUpdate.showInPopup,
				);

				if (hasNewUpdatesToBeOpenedInPopup) {
					return true;
				}

				return false;
			},
		});
	}

	saveProductUpdatesSeen() {
		void this.userSettingsService.save(
			UserSettingsKey.PRODUCT_UPDATES_SEEN_TIMESTAMP,
			moment().unix(),
		);
	}

	updateProductUpdate(data: ProductUpdate) {
		const productUpdate = this.productUpdates.find(
			(productUpdate) => productUpdate.id === data.id,
		);
		if (productUpdate) {
			this.productUpdates = this.productUpdates.filter((p) => p !== productUpdate);
		}
	}

	removeProductUpdate(id: number) {
		this.productUpdates = this.productUpdates.filter(
			(productUpdate) => productUpdate.id !== id,
		);
	}

	private createModal(): PopupModal {
		const popup = this.popupService.createModal(AccountPopupProductUpdatesComponent, {
			popupClass: 'popup-type-product-updates',
		});

		popup.on(PopupEvent.CLOSED, this, () => {
			this.analyticsService.event(TrackingEvent.PRODUCT_UPDATES_POPUP_CLOSED);
		});

		return popup;
	}
}

declare module '@ember/service' {
	interface Registry {
		'product-updates': ProductUpdatesService;
	}
}
